/**
 * EditFormPage.tsx
 * This page fetches a single pitch submission by its ID from the URL, pre-populates the edit form,
 * and handles updates using pitchesApi.update with Formik integration.
 */
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Skeleton, SkeletonFields } from "../components/ui/skeleton";
import { Formik, Form, FormikHelpers, FormikProps } from "formik";
import ReleaseInfo from "./components/ReleaseInfo/ReleaseInfo";
import PlatformSelection from "./components/PlatformSelector/PlatformSelection";
import GeneralReleaseInfo from "./components/GeneralReleaseInfo/GeneralReleaseInfo";
import DspSpecificInfo, { hasDspSpecificFields } from "./components/DSPSpecificInfo/DspSpecificInfo";
import { SelectedDSPs } from "../PitchFormPage/types/form";
import { createPitchValidationSchema, ProvisionalFieldHistory } from "./components/Schema/pitchValidationSchema";
import ProvisionalFieldsControl, { PROVISIONAL_FIELD_LABELS, PROVISIONAL_KEY_TO_FORMIK } from "./components/ProvisionalFields/ProvisionalFieldsControl";
import ProvisionalConfirmModal, { ChangedProvisionalField } from "./components/ProvisionalFields/ProvisionalConfirmModal";
import { dspDropdownOptions } from "./Data/dropdownOptions";
import { splitAnswer } from "./utils/optionRules";

import { pitchesApi } from "../repositories/pitches";
import * as Tooltip from "@radix-ui/react-tooltip";
import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from '../hooks/redux';
import Button from "../components/ui/Button";
import { swalColors } from "../utils/swal";
import OnInvalidSubmit from "../components/common/OnInvalidSubmit";
import Swal from "sweetalert2";
import { dspApi } from "../repositories/dsp";
import { useDspArtists } from "../hooks/useDSpArtists";
import { validateSelectedDSPs } from "./utils/dspValidation";
import { handleUpcConflict } from "./utils/upcConflict";
import { soundchartsEndpoints } from '../repositories/soundcharts';
import { oauthApi } from "../repositories/oauth";
import EmailPitchModal from "../components/modal/EmailPitchModal";
import { EmailContact, PersonalizedMessage } from "../types/email";
import contactApi from "../repositories/contact";
import templateApi from "../repositories/template";
import { fromBase64 } from "../utils/base64";
import { downloadAppleCsv } from "../utils/pitchCsvDownload";
import { selectCompanyType } from "../store/slices/userSlice";
import { classifyCompanyType } from "../utils/companyTypeClassification";
import Breadcrumbs, { BreadcrumbItem, HOME_CRUMB } from "../components/ui/Breadcrumbs";
const EditFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Back/Cancel and the breadcrumb trail both return to wherever the edit was launched from.
  // The launching link tags itself with ?from= (and, for analytics, the source it was itself
  // opened with) so the pitch id can rebuild that exact URL - a blind history pop would break
  // on deep links. Both the "from" origin and the resolved parent URL are derived once here.
  const editParams = new URLSearchParams(window.location.search);
  const cameFromAnalytics = editParams.get("from") === "analytics";
  const cameFromDashboard = editParams.get("from") === "dashboard";
  const analyticsSource = editParams.get("source") || "past-submissions";
  // Past Submissions' page number rides along the whole chain so every step
  // back - and the breadcrumb straight to the list - returns to that page.
  const returnPage = editParams.get("page");
  const pageQuery = returnPage ? `&page=${encodeURIComponent(returnPage)}` : "";
  const pastSubmissionsTo = `/past-submissions${
    returnPage ? `?page=${encodeURIComponent(returnPage)}` : ""
  }`;
  const parentUrl = cameFromDashboard
    ? "/dashboard"
    : cameFromAnalytics
    ? `/analytics/detail/${id}?source=${analyticsSource}${pageQuery}`
    : `/pitch/submission/${id}${returnPage ? `?page=${encodeURIComponent(returnPage)}` : ""}`;

  const breadcrumbItems: BreadcrumbItem[] = cameFromDashboard
    ? [
        HOME_CRUMB,
        { label: "Edit Pitch" },
      ]
    : cameFromAnalytics
    ? [
        HOME_CRUMB,
        // The track analytics page itself was opened from one of these two lists.
        analyticsSource === "analytics"
          ? { label: "Analytics", to: "/analytics" }
          : { label: "Past Submissions", to: pastSubmissionsTo },
        { label: "Track Analytics", to: parentUrl },
        { label: "Edit Pitch" },
      ]
    : [
        HOME_CRUMB,
        { label: "Past Submissions", to: pastSubmissionsTo },
        { label: "Review Pitch", to: parentUrl },
        { label: "Edit Pitch" },
      ];

  const handleBack = () => {
    navigate(parentUrl);
  };
  const { user } = useAppSelector((state) => state.auth);
  const companyType = useAppSelector(selectCompanyType);
  const companyTypeCategory = classifyCompanyType(companyType);


  const [loading, setLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDrafting, setIsDrafting] = useState<boolean>(false);
  const [showArtistUpdatePrompt, setShowArtistUpdatePrompt] = useState<boolean>(false);
  const [artistUpdates, setArtistUpdates] = useState<any>(null);
  const [currentPitchData, setCurrentPitchData] = useState<any>(null);
  // Remove showErrorSummary state since we'll use toast instead

  // Provisional confirm modal state
  const [provisionalModalData, setProvisionalModalData] = useState<{
    changedFields: ChangedProvisionalField[];
    unchangedFields: { key: string; label: string }[];
    pendingSubmitPayload: any;
  } | null>(null);

  // Email pitch modal states
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState<any>(null);
  // Track if email data has been configured (for showing Edit Email button)
  const [hasEmailData, setHasEmailData] = useState(false);
  // Store email data from modal (contacts, template, subject)
  const [emailData, setEmailData] = useState<{
    contactIds: number[];
    templateId: string;
    subject: string;
    templateContent?: string;
    selectedContacts?: EmailContact[];
    personalizedMessages?: PersonalizedMessage[];
  } | null>(null);

  // Memoized so the modal's open/reset effect (which lists initialEmailData as a
  // dependency) doesn't re-fire on every render and wipe in-progress selections.
  const initialEmailData = useMemo(
    () =>
      hasEmailData && emailData
        ? {
          contactIds: emailData.contactIds,
          templateId: emailData.templateId,
          subject: emailData.subject,
          personalizedMessages: emailData.personalizedMessages,
        }
        : undefined,
    [
      hasEmailData,
      emailData?.contactIds,
      emailData?.templateId,
      emailData?.subject,
      emailData?.personalizedMessages,
    ]
  );


  // Use the DSP artists hook to get connected accounts
  const { dspList, fetchDSPs } = useDspArtists();

  // Formik ref to access form values
  const formikRef = useRef<FormikProps<any>>(null);

  // Baseline for the two pieces of pitch state that live outside Formik
  // (platform checkboxes and the email modal's data). Formik tracks its own
  // values via `dirty`; these need snapshotting at load time so the submit
  // button can tell whether anything actually changed.
  const [baselineSelectedDSPs, setBaselineSelectedDSPs] = useState<string>("");
  const [baselineEmailData, setBaselineEmailData] = useState<string>("");

  // Serialize the email fields that a user can actually edit in the modal.
  const serializeEmailData = (data: typeof emailData): string => {
    if (!data) return "";
    return JSON.stringify({
      contactIds: [...(data.contactIds ?? [])].map(String).sort(),
      templateId: data.templateId ?? "",
      subject: data.subject ?? "",
      personalizedMessages: [...(data.personalizedMessages ?? [])]
        .map((m) => ({ contactId: String(m.contactId), message: m.message ?? "" }))
        .sort((a, b) => a.contactId.localeCompare(b.contactId)),
    });
  };

  // DSP mapping - maps selectedDSPs keys to display names
  const dspMapping: Record<keyof SelectedDSPs, string> = {
    spotify: "Spotify",
    amazon: "Amazon Music",
    deezer: "Deezer",
    audiomack: "Audiomack",
    apple: "Apple Music",
    TIDAL: "TIDAL",
    youtube: "YouTube",
    soundcloud: "SoundCloud",
    email: "Email",
    siriusxm: "SiriusXM",
    vevo: "Vevo"
  };

  // Handler for DSP deselection warning
  const handleDspDeselect = async (dspKey: keyof SelectedDSPs): Promise<boolean> => {
    const dspName = dspMapping[dspKey];
    const formValues = formikRef.current?.values;

    // Check if there are marketing notes for this DSP
    const dspNotes = formValues?.dspMarketingNotes || [];
    const hasNote = dspNotes.some((note: any) => note.dspName === dspName);

    // Check if there are platform-specific UPCs for this DSP
    const dspUpcs = formValues?.platformSpecificUpcs || [];
    const matchingUpcs = dspUpcs.filter((entry: any) => entry.dspName === dspName);
    const hasUpc = matchingUpcs.length > 0;

    if (hasNote || hasUpc) {
      const items: string[] = [];
      if (hasNote) items.push("marketing notes");
      if (hasUpc) items.push(`${matchingUpcs.length} platform-specific UPC(s)`);

      // Show confirmation modal
      const result = await Swal.fire({
        title: 'Warning',
        html: `You have ${items.join(" and ")} for <strong>${dspName}</strong>. If you deselect this platform, they will be deleted. Continue?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove and Deselect',
        cancelButtonText: 'Keep Platform Selected',
        confirmButtonColor: swalColors.danger,
        reverseButtons: true
      });

      if (result.isConfirmed) {
        if (hasNote) {
          const updatedNotes = dspNotes.filter((note: any) => note.dspName !== dspName);
          formikRef.current?.setFieldValue('dspMarketingNotes', updatedNotes);
        }
        if (hasUpc) {
          const updatedUpcs = dspUpcs.filter((entry: any) => entry.dspName !== dspName);
          formikRef.current?.setFieldValue('platformSpecificUpcs', updatedUpcs);
        }
        return true; // Proceed with deselection
      } else {
        return false; // Cancel deselection
      }
    }

    return true; // No notes or UPCs, proceed with deselection
  };

  // Fetch connected DSP accounts on mount
  useEffect(() => {
    fetchDSPs();
  }, []);


  // Initialize empty form data
  const [initialFormValues, setInitialFormValues] = useState<any>({
    // Basic Artist and Release Information
    artistName: "",
    releaseTitle: "",
    releaseDate: "",
    configuration: [],

    // Identifiers
    releaseUpc: "",
    focusTrackName: "",
    trackIsrc: "",

    // Artist Details
    featuredArtists: "",
    featuredArtistUri: "",
    artistHometown: "",
    artistCurrentTown: "",

    // Release Descriptions
    altTitles: "",
    shortDescription: "",
    expandedDescription: "",
    longDescription: "",
    lyrics: "",
    songDescription: [],

    // Musical Characteristics
    mainGenre: [],
    songMood: [],
    language: [],

    // Links and Media
    spotifyUri: "",
    linkToListen: "",
    promoPhotos: "",
    promoPhotosCredit: "",
    images: [],
    socialsLinks: "",

    // Platform-Specific Details
    similarArtists: "",
    asin: "",
    TIDALpage: "",
    soundCloudProfileUrl: "",
    soundCloudContact: "",
    distributor: "",
    appleId: "",
    aplleTrackId: "",
    appleAlbumId: "",
    appleSubgenre: [],
    appleReleaseStage: "",
    applePitchFrom: "",
    applePitchTo: "",
    appleHasTimedRelease: "",
    appleTimedReleaseDate: "",
    appleHasMotionArtwork: "",
    appleHasSpatialAudio: "",
    appleLabel: "",
    applePromotionDate: "",
    appleCountry: "",

    // Platform-Specific Flags and Priorities
    pinToThisIsPlaylist: false,
    pitchToAudiomackTrending: false,
    isPremierProgram: false,

    // Subgenres
    spotifySubgenre: [],
    amazonSubgenre: [],
    deezerSubgenre: [],
    audiomackSubgenre: [],
    TIDALSubgenre: [],
    soundCloudSubgenre: [],

    // Spotify specific
    musicCulture: [],
    instruments: [],
    songStyle: [],
    spotifyPlaylist: [],

    // Amazon specific
    listenersLocation: [],
    songCategory: [],

    // Deezer specific
    priority: "",
    deezerEditorialTeam: "",
    deezerPlaylist: [],

    // TIDAL specific
    artistCountry: "",
    LabelDistributor: "",
    tidalArtistLevel: "",
    tidalPriorityLevel: "",
    tidalDolbyAtmos: "",
    tidalLocalMarket: [],
    tidalBpm: 0,
    tidalConfirmation: null,

    // SoundCloud specific
    promotionTerritories: [],
    freshMusicFriday: [],
    playlistConsideration: "",
    soundCloudReleaseStage: "",
    soundCloudLabel: "",

    // Status
    status: "",
    // True when the pitch has previously sent emails (from GET /pitches/get-pitch)
    hasEmailLogs: false,

    // YouTube specific
    youtubePromotionSurface: [],
    // youtubeSongDetails: [],
    youtubeReleaseStage: "",
    youtubePromotionDate: "",
    youtubeTerritories: [],

    //siriusxm specific
    songWriters: "",
    siriusXmChannel: "",
    canadianContent: "",
    //contributors
    contributors: [],

    // Vevo specific fields
    vevoContentType: "",
    vevoPremiereDate: "",
    vevoContentProvider: "",
    vevoSubgenre: [],
    vevoSubgenreOther: "",
    vevoAdditionalNotes: "",
    vevoPreviewLink: "",
    vevoRep: "",
    vevoRepOther: "",
    vevoIsCtvOnly: "",
    vevoIsRefeature: "",

    // Provisional fields
    provisionalFields: [],
    provisionalNote: "",
    provisionalFieldHistory: [],

    // Hydrated from the saved pitch; overwritten by Apply Suggestions
    aimsRequestId: undefined,
  });

  const [selectedDSPs, setSelectedDSPs] = useState<SelectedDSPs>({
    spotify: false,
    amazon: false,
    deezer: false,
    TIDAL: false,
    audiomack: false,
    soundcloud: false,
    apple: false,
    youtube: false,
    email: false,
    siriusxm: false,
    vevo: false,
  });

  // Helper function to convert string to array
  const stringToArray = (value: string | null | undefined): string[] => {
    if (!value) return [];
    return value.split(',').map(item => item.trim()).filter(item => item.length > 0);
  };

  // Check for artist updates
  const checkForArtistUpdates = async (pitchData: any) => {
    if (!pitchData.artistName || pitchData.status !== 'draft') {
      return;
    }

    try {

      const response = await pitchesApi.checkArtistUpdates(Number(id), pitchData.artistName);
      if (response.data.data.hasUpdates) {
        setArtistUpdates(response.data);
        setShowArtistUpdatePrompt(true);
      }
    } catch (error) {
      console.error("❌ Error checking artist updates:", error);
    }
  };

  // Apply artist updates to form
  const applyArtistUpdates = async () => {
    try {
      setLoading(true);

      const response = await pitchesApi.syncArtistData(Number(id), currentPitchData.artistName);
      if (response.data.success) {
        await fetchSubmission();

        Swal.fire({
          title: 'Artist Data Updated!',
          text: 'Your draft has been updated with the latest artist information.',
          icon: 'success',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error("❌ Error syncing artist data:", error);
      Swal.fire({
        title: 'Update Failed',
        text: 'Failed to update artist data. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setLoading(false);
      setShowArtistUpdatePrompt(false);
    }
  };

  // Fetch submission data when the component mounts
  const fetchSubmission = async () => {
    try {
      const response = await pitchesApi.getOne(Number(id));
      const { data } = response.data;
      setCurrentPitchData(data);
      
      // Extract DSP Marketing Notes from individual DSP info objects
      const dspMarketingNotes: any[] = [];
      
      // Check Spotify marketing note
      if (data.SpotifyInfo?.marketingNote) {
        dspMarketingNotes.push({
          id: `dsp-note-spotify-${Date.now()}`,
          dspName: "Spotify",
          note: data.SpotifyInfo.marketingNote
        });
      }
      
      // Check Amazon marketing note
      if (data.AmazonInfo?.marketingNote) {
        dspMarketingNotes.push({
          id: `dsp-note-amazon-${Date.now()}`,
          dspName: "Amazon Music",
          note: data.AmazonInfo.marketingNote
        });
      }
      
      // Check Deezer marketing note
      if (data.DeezerInfo?.marketingNote) {
        dspMarketingNotes.push({
          id: `dsp-note-deezer-${Date.now()}`,
          dspName: "Deezer",
          note: data.DeezerInfo.marketingNote
        });
      }
      
      // Check TIDAL marketing note
      if (data.TidalInfo?.marketingNote) {
        dspMarketingNotes.push({
          id: `dsp-note-tidal-${Date.now()}`,
          dspName: "TIDAL",
          note: data.TidalInfo.marketingNote
        });
      }
      
      // Check Audiomack marketing note
      if (data.AudiomackInfo?.marketingNote) {
        dspMarketingNotes.push({
          id: `dsp-note-audiomack-${Date.now()}`,
          dspName: "Audiomack",
          note: data.AudiomackInfo.marketingNote
        });
      }
      
      // Check SoundCloud marketing note
      if (data.SoundCloudInfo?.marketingNote) {
        dspMarketingNotes.push({
          id: `dsp-note-soundcloud-${Date.now()}`,
          dspName: "SoundCloud",
          note: data.SoundCloudInfo.marketingNote
        });
      }
      
      // Check Apple marketing note
      if (data.AppleInfo?.marketingNote) {
        dspMarketingNotes.push({
          id: `dsp-note-apple-${Date.now()}`,
          dspName: "Apple Music",
          note: data.AppleInfo.marketingNote
        });
      }
      
      // Check YouTube marketing note
      if (data.YoutubeInfo?.marketingNote) {
        dspMarketingNotes.push({
          id: `dsp-note-youtube-${Date.now()}`,
          dspName: "YouTube",
          note: data.YoutubeInfo.marketingNote
        });
      }
      
      // Check SiriusXM marketing note
      if (data.SiriusXMInfo?.marketingNote) {
        dspMarketingNotes.push({
          id: `dsp-note-siriusxm-${Date.now()}`,
          dspName: "SiriusXM",
          note: data.SiriusXMInfo.marketingNote
        });
      }

      // Check Vevo marketing note
      if (data.VevoInfo?.marketingNote) {
        dspMarketingNotes.push({
          id: `dsp-note-vevo-${Date.now()}`,
          dspName: "Vevo",
          note: data.VevoInfo.marketingNote
        });
      }

      // Extract platform-specific UPCs from individual DSP info objects
      const platformSpecificUpcs: any[] = [];
      const dspInfoMap: Record<string, string> = {
        SpotifyInfo: "Spotify",
        AmazonInfo: "Amazon Music",
        DeezerInfo: "Deezer",
        TidalInfo: "TIDAL",
        AudiomackInfo: "Audiomack",
        SoundCloudInfo: "SoundCloud",
        AppleInfo: "Apple Music",
        YoutubeInfo: "YouTube",
        SiriusXMInfo: "SiriusXM",
        VevoInfo: "Vevo",
      };

      Object.entries(dspInfoMap).forEach(([infoKey, dspName]) => {
        const dspInfo = data[infoKey];
        if (dspInfo?.platformSpecificUpcs && Array.isArray(dspInfo.platformSpecificUpcs)) {
          dspInfo.platformSpecificUpcs.forEach((entry: any, i: number) => {
            platformSpecificUpcs.push({
              id: `platform-upc-${dspName.toLowerCase().replace(/\s/g, '-')}-${Date.now()}-${i}`,
              dspName,
              upc: entry.upc,
              notes: entry.notes || "",
            });
          });
        }
      });

      // Also check top-level platformSpecificUpcs (if backend stores it there)
      if (data.platformSpecificUpcs && Array.isArray(data.platformSpecificUpcs)) {
        data.platformSpecificUpcs.forEach((entry: any, i: number) => {
          // Avoid duplicates if already extracted from DSP info
          if (!platformSpecificUpcs.some((e: any) => e.upc === entry.upc && e.dspName === (entry.dspName || entry.dsp))) {
            platformSpecificUpcs.push({
              id: `platform-upc-top-${Date.now()}-${i}`,
              dspName: entry.dspName || entry.dsp,
              upc: entry.upc,
              notes: entry.notes || "",
            });
          }
        });
      }

      const formValues: any = {
        // Basic Information
        artistName: data.artistName || "",
        releaseTitle: data.releaseTitle || "",
        releaseDate: data.releaseDate || "",
        configuration: data.configuration
          ? (typeof data.configuration === 'string'
              ? data.configuration.split(', ').map((s: string) => s.trim()).filter((s: string) => s.length > 0)
              : data.configuration)
          : [],
        releaseUpc: data.releaseUPC || "",
        focusTrackName: data.focusTrackName || "",
        trackIsrc: data.trackISRC || "",
        featuredArtists: data.featuredArtists || "",
        featuredArtistUri: data.featuredArtistURI || "",
        altTitles: data.altTitles || "",
        shortDescription: data.shortDescription || "",
        expandedDescription: data.expandedDescription || "",
        longDescription: data.longDescription || "",
        lyrics: data.lyrics || "",
        songDescription: stringToArray(data.songDescription),
        mainGenre: stringToArray(data.mainGenre),
        songMood: stringToArray(data.songMood),
        language: stringToArray(data.language),
        linkToListen: data.linkToListen || "",
        promoPhotos: data.promoPhotos || "",
        promoPhotosCredit: data.promoPhotosCredit || "",
        images: Array.isArray(data.images) ? data.images : [],
        status: data.status || "",
        hasEmailLogs: Boolean(data.hasEmailLogs),

        // Spotify Info
        spotifyUri: data.SpotifyInfo?.spotifyUri || "",
        artistHometown: data.SpotifyInfo?.artistHometown || "",
        artistCurrentTown: data.SpotifyInfo?.artistCurrentTown || "",
        pinToThisIsPlaylist: data.SpotifyInfo?.pinToPlaylist || false,
        spotifySubgenre: stringToArray(data.SpotifyInfo?.subgenre),
        musicCulture: stringToArray(data.SpotifyInfo?.musicCulture),
        instruments: stringToArray(data.SpotifyInfo?.instruments),
        songStyle: stringToArray(data.SpotifyInfo?.songStyle),
        spotifyPlaylist: (() => {
          const result = stringToArray(data.SpotifyInfo?.playlist);
          return result;
        })(),
        // Notable fields
        pitchToNotable: data.SpotifyInfo?.pitchToNotable || false,
        notableSubmissionCategory: data.SpotifyInfo?.notableSubmissionCategory || "",
        notableGlobalMusicProgram: stringToArray(data.SpotifyInfo?.notableGlobalMusicProgram),
        notableSubgenre: data.SpotifyInfo?.notableSubgenre || "",
        notableSongwriter: data.SpotifyInfo?.notableSongwriter || "",

        // Amazon Info
        listenersLocation: stringToArray(data.AmazonInfo?.listenersLocation),
        similarArtists: data.AmazonInfo?.similarArtists || "",
        // "Work, Study, & Reading" contains commas; keep it one option.
        songCategory: splitAnswer(data.AmazonInfo?.songCategory, dspDropdownOptions.songCategory),
        amazonSubgenre: stringToArray(data.AmazonInfo?.subgenre),
        asin: data.AmazonInfo?.asin || "",

        // Deezer Info
        priority: data.DeezerInfo?.priority || "",
        deezerEditorialTeam: data.DeezerInfo?.editorialTeam || "",
        deezerSubgenre: stringToArray(data.DeezerInfo?.subgenre),
        deezerPlaylist: stringToArray(data.DeezerInfo?.playlist),

        // TIDAL Info
        TIDALSubgenre: stringToArray(data.TidalInfo?.subgenre),
        TIDALpage: data.TidalInfo?.artistPageLink || "",
        artistCountry: data.TidalInfo?.country || "",
        LabelDistributor: data.TidalInfo?.labelDistributor || "",
        socialsLinks: data.TidalInfo?.socialHandles || "",
        tidalArtistLevel: data.TidalInfo?.artistLevel || "",
        tidalPriorityLevel: data.TidalInfo?.priorityLevel || "",
        tidalDolbyAtmos: data.TidalInfo?.dolbyAtmos || "",
        tidalLocalMarket: stringToArray(data.TidalInfo?.localMarket),
        tidalBpm: data.TidalInfo?.bpm || 0,
        tidalConfirmation: data.TidalInfo?.confirmation || null,

        // Audiomack Info
        pitchToAudiomackTrending: data.AudiomackInfo?.pitchToTrending || false,
        audiomackSubgenre: stringToArray(data.AudiomackInfo?.subgenre),

        // SoundCloud Info
        soundCloudProfileUrl: data.SoundCloudInfo?.soundCloudProfileUrl || "",
        soundCloudContact: data.SoundCloudInfo?.soundCloudContact || "",
        isPremierProgram: data.SoundCloudInfo?.isPremierProgram || false,
        distributor: data.SoundCloudInfo?.distributor || data.DeezerInfo?.distributor || "",
        soundCloudLabel: data.SoundCloudInfo?.label || data.DeezerInfo?.label || "",
        promotionTerritories: stringToArray(data.SoundCloudInfo?.promotionTerritories),
        soundCloudSubgenre: stringToArray(data.SoundCloudInfo?.subgenre),
        soundCloudReleaseStage: data.SoundCloudInfo?.releaseStage || "",
        freshMusicFriday: stringToArray(data.SoundCloudInfo?.freshMusicFriday),
        playlistConsideration: data.SoundCloudInfo?.playlistConsideration || "",

        // Apple Info
        appleId: data.AppleInfo?.appleId || "",
        appleTrackId: data.AppleInfo?.appleTrackId || "",
        appleAlbumId: data.AppleInfo?.appleAlbumId || "",
        appleSubgenre: stringToArray(data.AppleInfo?.subgenre),
        appleReleaseStage: data.AppleInfo?.releaseStage || "",
        applePitchFrom: data.AppleInfo?.pitchFrom || "",
        applePitchTo: data.AppleInfo?.pitchTo || "",
        appleHasTimedRelease: data.AppleInfo?.hasTimedRelease || "",
        appleTimedReleaseDate: data.AppleInfo?.timedReleaseDate ? new Date(data.AppleInfo.timedReleaseDate).toISOString().split('T')[0] : "",
        appleHasMotionArtwork: data.AppleInfo?.hasMotionArtwork || "",
        appleHasSpatialAudio: data.AppleInfo?.hasSpatialAudio || "",
        appleLabel: data.AppleInfo?.label || "",
        applePromotionDate: data.AppleInfo?.promotionDate ? new Date(data.AppleInfo.promotionDate).toISOString().split('T')[0] : "",
        appleCountry: data.AppleInfo?.country || "",

        // YouTube Info
        youtubePromotionSurface: stringToArray(data.YoutubeInfo?.promotionSurface),
        youtubeReleaseStage: data.YoutubeInfo?.songDetails || "",
        // youtubeSongDetails: stringToArray(data.YouTubeInfo?.songDetails),
        youtubePromotionDate: data.YoutubeInfo?.promotionDate ? new Date(data.YoutubeInfo.promotionDate).toISOString().split('T')[0] : "",
        youtubeTerritories: stringToArray(data.YoutubeInfo?.territories),
        // SiriusXM Info
        songWriters: data.SiriusXMInfo?.songWriters || "",
        siriusXmChannel: data.SiriusXMInfo?.siriusXmChannel || "",
        canadianContent: data.SiriusXMInfo?.canadianContent || false,
        // Vevo Info
        vevoContentType: data.VevoInfo?.contentType || "",
        vevoPremiereDate: data.VevoInfo?.premiereDate ? new Date(data.VevoInfo.premiereDate).toISOString().split('T')[0] : "",
        vevoContentProvider: data.VevoInfo?.contentProvider || "",
        vevoSubgenre: stringToArray(data.VevoInfo?.subgenre),
        vevoSubgenreOther: data.VevoInfo?.subgenreOther || "",
        vevoAdditionalNotes: data.VevoInfo?.additionalNotes || "",
        vevoPreviewLink: data.VevoInfo?.previewLink || "",
        vevoRep: data.VevoInfo?.rep || "",
        vevoRepOther: data.VevoInfo?.repOther || "",
        vevoIsCtvOnly: data.VevoInfo?.isCtvOnly || "",
        vevoIsRefeature: data.VevoInfo?.isRefeature || "",
        // Contributors - keep backend IDs or add temporary ones for new contributors
        contributors: Array.isArray(data.contributors)
          ? data.contributors.map((c: any, index: number) => {
            // Backend sends 'role' (singular) as an array, convert to comma-separated string
            const roleData = c.role || c.roles || [];
            const rolesString = Array.isArray(roleData) ? roleData.join(', ') : String(roleData);
            return {
              name: c.name,
              roles: rolesString,
              id: c.id ? String(c.id) : `temp-${Date.now()}-${index}`
            };
          })
          : [],
      };

      // Add the DSP marketing notes
      formValues.dspMarketingNotes = dspMarketingNotes;
      // Add the platform-specific UPCs
      formValues.platformSpecificUpcs = platformSpecificUpcs;
      // Restore provisional fields from API response
      formValues.provisionalFields = Array.isArray(data.provisionalFields) ? data.provisionalFields : [];
      formValues.provisionalNote = data.provisionalNote ?? "";
      formValues.provisionalFieldHistory = Array.isArray(data.provisionalFieldHistory) ? data.provisionalFieldHistory : [];
      // Restore the AIMS request ID so resubmitting an edit does not clear it
      formValues.aimsRequestId = data.aimsRequestId ?? undefined;

      setInitialFormValues(formValues);

      // Restore email draft data if PitchEmailDraft exists
      const pitchEmailDraft = data.PitchEmailDraft;
      const hasEmailDraft = Boolean(pitchEmailDraft?.draftData);

      const loadedDSPs: SelectedDSPs = {
        spotify: Boolean(data.SpotifyInfo),
        amazon: Boolean(data.AmazonInfo),
        deezer: Boolean(data.DeezerInfo),
        TIDAL: Boolean(data.TidalInfo),
        audiomack: Boolean(data.AudiomackInfo),
        soundcloud: Boolean(data.SoundCloudInfo),
        apple: Boolean(data.AppleInfo),
        youtube: Boolean(data.YoutubeInfo),
        email: hasEmailDraft,
        siriusxm: Boolean(data.SiriusXMInfo),
        vevo: Boolean(data.VevoInfo),
      };
      setSelectedDSPs(loadedDSPs);
      setBaselineSelectedDSPs(JSON.stringify(loadedDSPs));
      // Default baseline for pitches with no email draft; overwritten below
      // once the draft's contacts/template have been resolved.
      setBaselineEmailData("");

      if (hasEmailDraft) {
        const draft = pitchEmailDraft.draftData;
        const contactIds = Object.keys(draft.contacts || {});

        // Build personalized messages from the contacts map
        const personalizedMessages: PersonalizedMessage[] = contactIds
          .filter((cId) => draft.contacts[cId])
          .map((cId) => ({
            contactId: cId as any,
            message: draft.contacts[cId],
          }));

        // Fetch contacts and template in parallel so the email modal can show recipients and per-recipient notes
        try {
          const [contactsRes, templateRes] = await Promise.all([
            contactApi.getContacts({ page: 1, limit: 100 }),
            draft.templateId ? templateApi.getTemplate(draft.templateId) : Promise.resolve(null),
          ]);

          const allContacts: EmailContact[] = contactsRes?.data?.contacts || [];
          const selectedContacts = allContacts.filter((c: EmailContact) =>
            contactIds.includes(String(c.id))
          );

          // Build templateContent from decoded template parts
          let templateContent: string | undefined;
          if (templateRes?.data?.success) {
            const tmpl = templateRes.data.template;
            templateContent = [
              fromBase64(tmpl.greetingText || ""),
              fromBase64(tmpl.pitchData || ""),
              fromBase64(tmpl.closingText || ""),
            ].join(" ");
          }

          const restored = {
            contactIds: contactIds as any[],
            templateId: draft.templateId || "",
            subject: draft.subject || "",
            personalizedMessages,
            selectedContacts,
            templateContent,
          };
          setEmailData(restored);
          setBaselineEmailData(serializeEmailData(restored));
        } catch {
          const restored = {
            contactIds: contactIds as any[],
            templateId: draft.templateId || "",
            subject: draft.subject || "",
            personalizedMessages,
          };
          setEmailData(restored);
          setBaselineEmailData(serializeEmailData(restored));
        }
        setHasEmailData(true);
      }

      // Check for artist updates after loading the data
      await checkForArtistUpdates(data);

    } catch (error) {
      setFetchError("Failed to fetch submission data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchSubmission();
    }
  }, [id]);
  // Transform the form data to match the API's expected payload
  function transformSubmissionData(values: any) {
   
    const dsps: any = {};

    // Get DSP marketing notes for easy lookup
    const dspMarketingNotes = values.dspMarketingNotes || [];
    const notesLookup = dspMarketingNotes.reduce((acc: any, note: any) => {
      acc[note.dspName] = note.note;
      return acc;
    }, {});

    // Get platform-specific UPCs for lookup (grouped by DSP name)
    const platformSpecificUpcs = values.platformSpecificUpcs || [];
    const upcLookup = platformSpecificUpcs.reduce((acc: any, entry: any) => {
      if (!acc[entry.dspName]) acc[entry.dspName] = [];
      acc[entry.dspName].push({ upc: entry.upc, notes: entry.notes || null });
      return acc;
    }, {});

    if (selectedDSPs.spotify) {
      dsps.spotify = {
        spotifyUri: values.spotifyUri,
        artistHometown: values.artistHometown,
        artistCurrentTown: values.artistCurrentTown,
        pinToPlaylist: values.pinToThisIsPlaylist,
        subgenre: values.spotifySubgenre?.join(", ") || "",
        musicCulture: values.musicCulture?.join(", ") || "",
        instruments: values.instruments?.join(", ") || "",
        songStyle: values.songStyle?.join(", ") || "",
        playlist: values.spotifyPlaylist?.join(", ") || "",
        // Add marketing note if exists
        marketingNote: notesLookup["Spotify"] || null,
        platformSpecificUpcs: upcLookup["Spotify"] || null,
        // Notable fields
        pitchToNotable: values.pitchToNotable || false,
        notableSubmissionCategory: values.notableSubmissionCategory || null,
        notableGlobalMusicProgram: values.notableGlobalMusicProgram?.join(", ") || null,
        notableSubgenre: values.notableSubgenre || null,
        notableSongwriter: values.notableSongwriter || null,
      };
    }

    if (selectedDSPs.amazon) {
      dsps.amazon = {
        listenersLocation: values.listenersLocation?.join(", ") || "",
        similarArtists: values.similarArtists,
        songCategory: values.songCategory?.join(", ") || "",
        subgenre: values.amazonSubgenre?.join(", ") || "",
        asin: values.asin,
        // Add marketing note if exists
        marketingNote: notesLookup["Amazon Music"] || null,
        platformSpecificUpcs: upcLookup["Amazon Music"] || null,
      };
    }

    if (selectedDSPs.deezer) {
      dsps.deezer = {
        priority: values.priority,
        editorialTeam: values.deezerEditorialTeam,
        subgenre: values.deezerSubgenre?.join(", ") || "",
        playlist: values.deezerPlaylist?.join(", ") || "",
        // Only include whichever field is actually visible for the user's
        // company type - a hidden field may still hold a stale value in
        // form state and must not be submitted.
        distributor: companyTypeCategory !== "distributor" ? values.distributor : undefined,
        label: companyTypeCategory !== "label" ? values.soundCloudLabel : undefined,
        // Add marketing note if exists
        marketingNote: notesLookup["Deezer"] || null,
        platformSpecificUpcs: upcLookup["Deezer"] || null,
      };
    }

    if (selectedDSPs.TIDAL) {
      dsps.tidal = {
        subgenre: values.TIDALSubgenre?.join(", ") || "",
        artistPageLink: values.TIDALpage,
        country: values.artistCountry,
        labelDistributor: values.LabelDistributor,
        socialHandles: values.socialsLinks,
        artistLevel: values.tidalArtistLevel,
        priorityLevel: values.tidalPriorityLevel,
        dolbyAtmos: values.tidalDolbyAtmos,
        localMarket: Array.isArray(values.tidalLocalMarket)
          ? values.tidalLocalMarket.join(", ")
          : values.tidalLocalMarket,
        bpm: values.tidalBpm,
        confirmation: values.tidalConfirmation ,
        // Add marketing note if exists
        marketingNote: notesLookup["TIDAL"] || null,
        platformSpecificUpcs: upcLookup["TIDAL"] || null,
      };
    }

    if (selectedDSPs.audiomack) {
      dsps.audiomack = {
        pitchToTrending: values.pitchToAudiomackTrending,
        subgenre: values.audiomackSubgenre?.join(", ") || "",
        // Add marketing note if exists
        marketingNote: notesLookup["Audiomack"] || null,
        platformSpecificUpcs: upcLookup["Audiomack"] || null,
      };
    }

    if (selectedDSPs.soundcloud) {
      dsps.soundcloud = {
        fullName: values.fullName,
        soundCloudProfileUrl: values.soundCloudProfileUrl,
        soundCloudContact: values.soundCloudContact,
        subgenre: values.soundCloudSubgenre?.join(", ") || "",
        isPremierProgram: values.isPremierProgram,
        // Only include whichever field is actually visible for the user's
        // company type - a hidden field may still hold a stale value in
        // form state and must not be submitted.
        distributor: companyTypeCategory !== "distributor" ? values.distributor : undefined,
        label: companyTypeCategory !== "label" ? values.soundCloudLabel : undefined,
        promotionTerritories: values.promotionTerritories?.join(", ") || "",
        releaseStage: values.soundCloudReleaseStage,
        freshMusicFriday: values.freshMusicFriday?.join(", ") || "",
        playlistConsideration: values.playlistConsideration,
        // Add marketing note if exists
        marketingNote: notesLookup["SoundCloud"] || null,
        platformSpecificUpcs: upcLookup["SoundCloud"] || null,
      };
    }

    if (selectedDSPs.apple) {
      dsps.apple = {
        appleId: values.appleId,
        appleTrackId: values.appleTrackId,
        appleAlbumId: values.appleAlbumId,
        subgenre: values.appleSubgenre?.join(", ") || "",
        releaseStage: values.appleReleaseStage,
        pitchFrom: values.applePitchFrom,
        pitchTo: values.applePitchTo,
        hasTimedRelease: values.appleHasTimedRelease,
        timedReleaseDate: values.appleTimedReleaseDate || null,
        hasMotionArtwork: values.appleHasMotionArtwork,
        hasSpatialAudio: values.appleHasSpatialAudio,
        label: values.appleLabel,
        promotionDate: values.applePromotionDate || null,
        country: values.appleCountry,
        // Add marketing note if exists
        marketingNote: notesLookup["Apple Music"] || null,
        platformSpecificUpcs: upcLookup["Apple Music"] || null,
      };
    }

    if (selectedDSPs.youtube) {
      dsps.youtube = {
        promotionSurface: values.youtubePromotionSurface?.join(", ") || "",
        songDetails: values.youtubeReleaseStage,
        promotionDate: values.youtubePromotionDate || null,
        territories: values.youtubeTerritories?.join(", ") || "",
        // Add marketing note if exists
        marketingNote: notesLookup["YouTube"] || null,
        platformSpecificUpcs: upcLookup["YouTube"] || null,
      };
    }

    if (selectedDSPs.siriusxm) {
      dsps.siriusxm = {
        songWriters: values.songWriters || "",
        siriusXmChannel: values.siriusXmChannel || "",
        canadianContent: values.canadianContent || false,
        // Add marketing note if exists
        marketingNote: notesLookup["SiriusXM"] || null,
        platformSpecificUpcs: upcLookup["SiriusXM"] || null,
      };
    }

    if (selectedDSPs.vevo) {
      dsps.vevo = {
        contentType: values.vevoContentType,
        premiereDate: values.vevoPremiereDate,
        contentProvider: values.vevoContentProvider,
        subgenre: Array.isArray(values.vevoSubgenre)
          ? values.vevoSubgenre.join(", ")
          : values.vevoSubgenre,
        subgenreOther: values.vevoSubgenreOther,
        additionalNotes: values.vevoAdditionalNotes,
        previewLink: values.vevoPreviewLink,
        rep: values.vevoRep,
        repOther: values.vevoRepOther,
        isCtvOnly: values.vevoIsCtvOnly,
        isRefeature: values.vevoIsRefeature,
        // Add marketing note if exists
        marketingNote: notesLookup["Vevo"] || null,
        platformSpecificUpcs: upcLookup["Vevo"] || null,
      };
    }

    return {
      artistName: values.artistName,
      releaseTitle: values.releaseTitle,
      releaseDate: values.releaseDate,
      configuration: Array.isArray(values.configuration)
        ? values.configuration.join(", ")
        : values.configuration,
      releaseUPC: values.releaseUpc || null,
      focusTrackName: values.focusTrackName,
      soundchartsUUID: values.soundchartsUUID,
      trackISRC: values.trackIsrc || null,
      featuredArtists: values.featuredArtists,
      featuredArtistURI: values.featuredArtistUri,
      altTitles: values.altTitles,
      shortDescription: values.shortDescription,
      expandedDescription: values.expandedDescription,
      longDescription: values.longDescription,
      lyrics: values.lyrics,
      songDescription: Array.isArray(values.songDescription)
        ? values.songDescription.join(", ")
        : values.songDescription,
      mainGenre: Array.isArray(values.mainGenre)
        ? values.mainGenre.join(", ")
        : values.mainGenre,
      songMood: Array.isArray(values.songMood)
        ? values.songMood.join(", ")
        : values.songMood,
      language: Array.isArray(values.language)
        ? values.language.join(", ")
        : values.language,
      linkToListen: values.linkToListen,
      promoPhotos: values.promoPhotos,
      promoPhotosCredit: values.promoPhotosCredit,
      images: Array.isArray(values.images) ? values.images : [],
      contributors: (values.contributors || []).map(({ id, name, roles }: any) => {
        const rolesArray = typeof roles === 'string'
          ? roles.split(',').map((r: string) => r.trim()).filter((r: string) => r.length > 0)
          : Array.isArray(roles) ? roles : [];

        const contributor: any = { name, role: rolesArray };
        if (id && !id.toString().startsWith('contributor-') && !id.toString().startsWith('temp-')) {
          contributor.id = id;
        }
        return contributor;
      }),
      status: values.status,
      dsps: dsps,
      isEmail: selectedDSPs.email,
      platformSpecificUpcs: values.platformSpecificUpcs || [],
      provisionalFields: values.provisionalFields ?? [],
      provisionalNote: values.provisionalNote ?? null,
      provisionalFieldHistory: values.provisionalFieldHistory ?? [],
      aimsRequestId: values.aimsRequestId ?? null,
    };
  }

  // Function to scroll to the first error
  const scrollToFirstError = (errors: any) => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return;

    const firstErrorKey = errorKeys[0];
    const errorField = document.querySelector(`[name="${firstErrorKey}"]`);

    if (errorField) {
      errorField.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      errorField.classList.add('ring-2', 'ring-danger-500', 'bg-danger-50');
      setTimeout(() => {
        errorField.classList.remove('ring-2', 'ring-danger-500', 'bg-danger-50');
      }, 3000);
    }
  };

  // Function to show error toast
  const showErrorToast = (errors: any) => {
    const errorCount = Object.keys(errors).length;
    const errorFields = Object.keys(errors).slice(0, 3).join(', ');
    const remainingCount = errorCount > 3 ? errorCount - 3 : 0;

    let errorMessage = `Please fix ${errorCount} validation error${errorCount > 1 ? 's' : ''}.`;
    if (errorCount <= 3) {
      errorMessage += ` Fields: ${errorFields}`;
    } else {
      errorMessage += ` Fields: ${errorFields}${remainingCount > 0 ? ` and ${remainingCount} more` : ''}`;
    }

    Swal.fire({
      title: 'Validation Errors',
      text: errorMessage,
      icon: 'error',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer)
        toast.addEventListener('mouseleave', Swal.resumeTimer)
      }
    });
  };

  // Check OAuth status for email
  const checkEmailOAuthStatus = async (): Promise<boolean> => {
    try {
      const response = await oauthApi.getEmailStatus();
      return response?.data?.gmail?.connected === true || response?.data?.outlook?.connected === true;
    } catch (error) {
      console.error('Failed to check OAuth status:', error);
      return false;
    }
  };

  // Handle email modal send - just store data and close modal (no API hit)
  const handleEmailSend = async (data: {
    contactIds: number[];
    templateId: string;
    subject: string;
    templateContent?: string;
    selectedContacts?: EmailContact[];
    personalizedMessages?: PersonalizedMessage[];
  }) => {
    // The modal writes the messages itself when the template supports them; otherwise keep
    // whatever was staged. Either way, drop notes for contacts that are no longer selected.
    const existingMessages = data.personalizedMessages ?? emailData?.personalizedMessages ?? [];
    const preservedMessages = existingMessages.filter((m) =>
      data.contactIds.includes(m.contactId)
    );

    // Store the email data for later submission
    setEmailData({
      ...data,
      personalizedMessages: preservedMessages,
    });
    setHasEmailData(true);
    setIsEmailModalOpen(false);
    setPendingFormValues(null);

    // Show confirmation that email data is saved
    Swal.fire({
      title: 'Email Configured',
      text: data.contactIds.length > 0
        ? `Email settings saved. ${data.contactIds.length} contact${data.contactIds.length > 1 ? 's' : ''} will receive the pitch email when you submit.`
        : 'Email settings saved. Email will only be sent to you when you submit.',
      icon: 'success',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
  };

  // Handle opening email modal for editing (without triggering submit)
  const handleEditEmail = (values: any) => {
    setPendingFormValues(values);
    setIsEmailModalOpen(true);
  };

  // Handle Email checkbox selection - open modal immediately
  const handleEmailSelect = async () => {
    // If email is already configured, don't open modal again
    if (hasEmailData && emailData) {
      return;
    }

    // First check OAuth status
    const isOAuthConnected = await checkEmailOAuthStatus();

    if (!isOAuthConnected) {
      // Show error popup blocking selection
      Swal.fire({
        title: "Email Not Connected",
        text: "You need to connect your email account before sending email pitches. Please connect your Gmail or Outlook account in settings.",
        icon: "error",
        showCancelButton: true,
        confirmButtonText: "Connect Email",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/email");
        } else {
          // Uncheck email if user cancels
          setSelectedDSPs(prev => ({ ...prev, email: false }));
        }
      });
      return;
    }

    // OAuth connected - open the email modal
    setPendingFormValues(formikRef.current?.values || null);
    setIsEmailModalOpen(true);
  };

  // Submit pitch with email data (for draft submissions with email)
  const submitPitchWithEmail = async (
    values: any,
    emailData: {
     subject:string
          templateId: string, 
          contacts: object,
    }
  ) => {
    setIsSubmitting(true);

    try {
      let soundchartsUUID: string | null = null;
      if (values.trackIsrc) {
        try {
          const soundchartsResponse = await soundchartsEndpoints.getSongByISRC(values.trackIsrc);
          const artistUuid = soundchartsResponse?.data?.object?.artists?.[0]?.uuid;
          soundchartsUUID = artistUuid || null;
        } catch (scError) {
          console.error("Soundcharts API error:", scError);
        }
      }

      const submissionPayload = {
        ...values,
        status: "submitted",
        soundchartsUUID: soundchartsUUID
      };

      // Submit the pitch with email data included
      const Payload = {
        ...transformSubmissionData(submissionPayload),
        emailData
      };

      const response = await pitchesApi.submitDraftPitch(Number(id), Payload);

      if (!response?.data.success) {
        throw new Error(response.statusText || "Submission failed");
      }

      Swal.fire({
        title: "Success!",
        text: emailData.templateId
          ? `Pitch submitted and emails sent successfully!`
          : "Pitch submitted successfully!",
        icon: "success",
        confirmButtonText: "OK",
      });

      navigate("/dashboard");
    } catch (error: any) {
      console.error("Submission error:", error);

      if (await handleUpcConflict(error, navigate)) {
        return;
      }

      const errorMessages =
        error.response?.data?.details && Array.isArray(error.response.data.details)
          ? error.response.data.details
            .map((detail: { field: string; message: string }) =>
              `<b>${detail.field}</b>: ${detail.message}`
            )
            .join("<br>")
          : error.response?.data?.errors && Array.isArray(error.response.data.errors)
            ? error.response.data.errors
              .map((message: string) => `${message}`)
              .join("<br>")
            : error.response?.data?.message || "An unexpected error occurred";

      Swal.fire({
        title: "Error!",
        html: errorMessages,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSubmitting(false);
      setPendingFormValues(null);
    }
  };

  const handleSubmit = async (values: any, { setSubmitting }: FormikHelpers<any>) => {
    setIsSubmitting(true);

    try {
      
      const submissionPayload = {
        ...values,
        status: "submitted",
      };

      if (Object.keys(transformSubmissionData(submissionPayload).dsps).length === 0) {
        Swal.fire({
          title: "Error!",
          text: "Please select at least one platform to submit your pitch.",
          icon: "error",
          confirmButtonText: "OK",
        });
        setIsSubmitting(false);
        return;
      }

      // Admins can submit without connected DSP accounts; regular users must have them connected
      if (user?.role !== "admin") {
        // Validate that selected platforms have connected DSP accounts
        const validation = validateSelectedDSPs(selectedDSPs, dspList);
        if (!validation.isValid) {
          const missingPlatformsText = validation.missingPlatforms
            .map(p => p.toUpperCase())
            .join(', ');

          console.log(`⚠️ Warning: No connected accounts found for: ${missingPlatformsText}`);

          Swal.fire({
            title: "Error!",
            text: `You don't have connected accounts for: ${missingPlatformsText}. Please connect these DSPs before submitting a pitch.`,
            showCancelButton: true,
            cancelButtonText: "Cancel",
            confirmButtonText: "Connect DSPs",
          }).then(async (result) => {
            if (result.isConfirmed) {
              // Save draft before navigating to DSP settings
              try {
                const draftPayload = {
                  ...submissionPayload,
                  status: "draft"
                };
                await pitchesApi.updateDraft(
                  Number(id),
                  transformSubmissionData(draftPayload)
                );
              } catch (draftError) {
                console.error("Failed to update draft before navigation:", draftError);
                // Continue with navigation even if draft save fails
              }
              navigate("/settings?tab=dsps");
            }
          });
          setIsSubmitting(false);
          return;
        }

        try {
          const response = await dspApi.getDSPs();
          if (response?.data?.data?.length === 0) {
            Swal.fire({
              title: "Error!",
              text: "No DSPs connected. Please connect at least one DSP before submitting a pitch.",
              showCancelButton: true,
              cancelButtonText: "Cancel",
              confirmButtonText: "Connect DSPs",
            }).then(async (result) => {
              if (result.isConfirmed) {
                // Save draft before navigating to DSP settings
                try {
                  const draftPayload = {
                    ...submissionPayload,
                    status: "draft"
                  };
                  await pitchesApi.updateDraft(
                    Number(id),
                    transformSubmissionData(draftPayload)
                  );
                } catch (draftError) {
                  console.error("Failed to update draft before navigation:", draftError);
                  // Continue with navigation even if draft save fails
                }
                navigate("/settings?tab=dsps");
              }
            });
            setIsSubmitting(false);
            return;
          }

        } catch (error) {
          console.error("Error fetching DSPs:", error);
          Swal.fire({
            title: "Error!",
            text: "Failed to verify connected DSPs. Please try again later.",
            icon: "error",
            confirmButtonText: "OK",
          });
          setIsSubmitting(false);
          return;
        }
      }

      // Handle email submission for drafts, and for already-submitted pitches that
      // have not sent any email yet. Pitches with existing email logs are managed
      // from the Email page instead, so they are excluded here.
      if ((initialFormValues.status === 'draft' || !initialFormValues.hasEmailLogs) && selectedDSPs.email) {
        // Check if email data already exists (user already configured email via checkbox)
        if (hasEmailData && emailData) {
          // Validate subject is provided before submission
          if (!emailData.subject?.trim()) {
            Swal.fire({
              title: "Subject Required",
              text: "Please add an email subject before submitting. Click 'Edit Email' to add a subject.",
              icon: "warning",
              confirmButtonText: "OK",
            });
            setIsSubmitting(false);
            return;
          }

          const contactsMap: Record<string, string> = {};
          (emailData.selectedContacts || []).forEach((contact) => {
            contactsMap[String(contact.id)] =
              emailData.personalizedMessages?.find(
                (m) => m.contactId === contact.id
              )?.message || "";
          });
          const transformEmailData = {
            subject: emailData.subject,
            templateId: emailData.templateId,
            contacts: contactsMap,
          };

          if (initialFormValues.status === 'draft') {
            // Draft → submit-draft-pitch, which flips the pitch to submitted.
            await submitPitchWithEmail(values, transformEmailData);
            return;
          }

          // Already-submitted pitch → normal update path, so provisional-field
          // handling still runs. Carry emailData through to doSubmit.
          submissionPayload.emailData = transformEmailData;
        }
        else {

        // Email selected but not configured - prompt user to configure
        Swal.fire({
          title: "Email Not Configured",
          text: "Please configure your email settings by clicking the 'Edit Email' button or re-selecting the Email checkbox.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Configure Email",
          cancelButtonText: "Cancel",
        }).then((result) => {
          if (result.isConfirmed) {
            // Open email modal
            handleEmailSelect();
          }
        });
        setIsSubmitting(false);
        return;
        }
      }

      // If this is a non-draft pitch with provisional fields, detect which ones changed
      // and show the confirm modal before proceeding.
      if (
        initialFormValues.status !== "draft" &&
        (submissionPayload.provisionalFields?.length ?? 0) > 0
      ) {
        const normalize = (v: any): string =>
          Array.isArray(v) ? [...v].sort().join(",") : String(v ?? "");

        const changedFields: ChangedProvisionalField[] = [];
        for (const fieldKey of submissionPayload.provisionalFields) {
          // fieldKey may be namespaced ("spotify.artistHometown") — resolve to Formik key
          const formikKey = PROVISIONAL_KEY_TO_FORMIK[fieldKey] ?? fieldKey;
          const currentValue = (values as any)[formikKey];
          const originalFormValue = (initialFormValues as any)[formikKey];
          if (normalize(currentValue) !== normalize(originalFormValue)) {
            changedFields.push({
              key: fieldKey,
              label: PROVISIONAL_FIELD_LABELS[fieldKey] ?? fieldKey,
              oldValue: originalFormValue,
              newValue: currentValue,
            });
          }
        }

        if (changedFields.length > 0) {
          const changedKeys = new Set(changedFields.map(f => f.key));
          const unchangedFields = submissionPayload.provisionalFields
            .filter((k: string) => !changedKeys.has(k))
            .map((k: string) => ({ key: k, label: PROVISIONAL_FIELD_LABELS[k] ?? k }));

          // Deep-copy the payload so React state storage and any later Formik
          // re-renders cannot mutate the parked values before the modal resolves.
          setProvisionalModalData({
            changedFields,
            unchangedFields,
            pendingSubmitPayload: JSON.parse(JSON.stringify(submissionPayload)),
          });
          setIsSubmitting(false);
          return;
        }
      }

      await doSubmit(submissionPayload);
    } catch (error: any) {
      console.error("Update error:", error);

      if (await handleUpcConflict(error, navigate)) {
        return;
      }

      const errorMessages =
        error.response?.data?.details && Array.isArray(error.response.data.details)
          ? error.response.data.details
            .map((detail: { field: string; message: string }) =>
              `<b>${detail.field}</b>: ${detail.message}`
            )
            .join("<br>")
          : error.response?.data?.message || "An unexpected error occurred";

      Swal.fire({
        title: "Error!",
        html: errorMessages,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Called either directly (no changed provisional fields) or from the confirm modal.
  // modalFieldKeys = the fields the modal presented; stillProvisionalKeys = which of those the user kept as provisional.
  // initialSnapshot = a captured copy of initialFormValues at the time the modal opened (avoids stale-closure issues).
  const doSubmit = async (
    submissionPayload: any,
    stillProvisionalKeys?: string[],
    modalFieldKeys?: string[],
    initialSnapshot?: any,
  ) => {
    setIsSubmitting(true);
    // Use the explicit snapshot when called from the modal; fall back to the closure value for direct calls.
    const initial = initialSnapshot ?? initialFormValues;

    try {
      const normalize = (v: any): string =>
        Array.isArray(v) ? [...v].sort().join(",") : String(v ?? "");

      // originalProvisionalFields = what was provisional when the edit form loaded.
      const originalProvisionalFields: string[] = [...(initial.provisionalFields ?? [])];

      // Apply the modal's resolution.
      // stillProvisionalKeys only covers the fields the modal presented (the changed ones).
      // Unchanged provisional fields were never shown in the modal and must be preserved.
      if (stillProvisionalKeys !== undefined && modalFieldKeys !== undefined) {
        const modalKeySet = new Set(modalFieldKeys);
        submissionPayload.provisionalFields = (submissionPayload.provisionalFields ?? []).filter(
          (k: string) => !modalKeySet.has(k) || stillProvisionalKeys.includes(k)
        );
      }

      const provisionalFieldsDiff: ProvisionalFieldHistory[] = [];
      if (initial.status !== "draft") {
        const now = new Date().toISOString();
        const existingHistory: ProvisionalFieldHistory[] = initial.provisionalFieldHistory ?? [];
        const finalProvisionalSet = new Set(submissionPayload.provisionalFields ?? []);

        // Log every field that was originally provisional and has either changed value or been finalized (unchecked).
        // Fields presented in the modal (modalFieldKeys) are always logged.
        for (const fieldKey of originalProvisionalFields) {
          const formikKey = PROVISIONAL_KEY_TO_FORMIK[fieldKey] ?? fieldKey;
          const currentValue = submissionPayload[formikKey];
          const originalFormValue = (initial as any)[formikKey];
          const valueChanged = normalize(currentValue) !== normalize(originalFormValue);
          const wasFinalized = !finalProvisionalSet.has(fieldKey);
          const wasInModal = modalFieldKeys?.includes(fieldKey) ?? false;

          if (valueChanged || wasFinalized || wasInModal) {
            provisionalFieldsDiff.push({
              field: fieldKey,
              oldValue: originalFormValue,
              newValue: currentValue,
              updatedAt: now,
            });
          }
        }

        // Always assign so transformSubmissionData never sends a stale or null value.
        submissionPayload.provisionalFieldHistory = [
          ...existingHistory.filter((h: ProvisionalFieldHistory) => !provisionalFieldsDiff.some(d => d.field === h.field)),
          ...provisionalFieldsDiff,
        ];
      }

      let response;
      if (initial.status === "draft") {
        response = await pitchesApi.submitDraftPitch(
          Number(id),
          transformSubmissionData(submissionPayload)
        );
      } else {
        const updatePayload: any = transformSubmissionData(submissionPayload);
        // transformSubmissionData whitelists fields, so emailData must be re-attached.
        if (submissionPayload.emailData) {
          updatePayload.emailData = submissionPayload.emailData;
        }
        response = await pitchesApi.update(Number(id), updatePayload);
      }

      if (!response?.data.success) {
        throw new Error(response.statusText || "Update failed");
      }

      const isProvisionalCorrection =
        provisionalFieldsDiff.length > 0 && initial.status !== "draft";
      if (isProvisionalCorrection) {
        await Swal.fire({
          title: "Update Submitted",
          text: "Your update has been submitted. We will push the change to your selected platforms.",
          icon: "success",
          confirmButtonText: "OK",
        });
      }
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Submit error:", error);

      if (await handleUpcConflict(error, navigate)) {
        return;
      }

      const errorMessages =
        error.response?.data?.details && Array.isArray(error.response.data.details)
          ? error.response.data.details
            .map((detail: { field: string; message: string }) =>
              `<b>${detail.field}</b>: ${detail.message}`
            )
            .join("<br>")
          : error.response?.data?.message || "An unexpected error occurred";

      Swal.fire({
        title: "Error!",
        html: errorMessages,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async (values: any) => {
    setIsDrafting(true);

    try {
      const submissionPayload = {
        ...values,
        status: "draft",
      };

      const draftPayload: any = transformSubmissionData(submissionPayload);

      // Include email data if configured
      if (selectedDSPs.email && hasEmailData && emailData) {
        const contactsMap: Record<string, string> = {};
        (emailData.selectedContacts || []).forEach((contact) => {
          contactsMap[String(contact.id)] =
            emailData.personalizedMessages?.find(
              (m) => m.contactId === contact.id
            )?.message || "";
        });
        draftPayload.emailData = {
          subject: emailData.subject,
          templateId: emailData.templateId,
          contacts: contactsMap,
        };
      }

      const response = await pitchesApi.updateDraft(
        Number(id),
        draftPayload
      );

      if (!response?.data.success) {
        throw new Error(response.statusText || "Draft save failed");
      }

      Swal.fire({
        title: "Success!",
        text: "Draft saved successfully",
        icon: "success",
        confirmButtonText: "OK",
      });
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Draft save error:", error);

      if (await handleUpcConflict(error, navigate)) {
        return;
      }

      Swal.fire({
        title: "Error!",
        text: error.response?.data?.message || "Failed to save draft",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsDrafting(false);
    }
  };

  const handleRetry = () => {
    setFetchError("");
    setLoading(true);
    fetchSubmission();
  };

  const handleDownloadAppleCsv = () => {
    const values = formikRef.current?.values;
    if (!values) return;
    downloadAppleCsv({
      id: "",
      artistName: values.artistName,
      releaseTitle: values.releaseTitle,
      releaseDate: values.releaseDate,
      configuration: Array.isArray(values.configuration) ? values.configuration.join(", ") : values.configuration || "",
      releaseUPC: values.releaseUpc || "",
      focusTrackName: values.focusTrackName || "",
      trackISRC: values.trackIsrc || "",
      songDescription: Array.isArray(values.songDescription) ? values.songDescription.join(", ") : values.songDescription || "",
      mainGenre: Array.isArray(values.mainGenre) ? values.mainGenre.join(", ") : values.mainGenre || "",
      songMood: Array.isArray(values.songMood) ? values.songMood.join(", ") : values.songMood || "",
      language: Array.isArray(values.language) ? values.language.join(", ") : values.language || "",
      featuredArtists: values.featuredArtists || "",
      shortDescription: values.shortDescription || "",
      longDescription: values.longDescription || "",
      lyrics: values.lyrics || "",
      linkToListen: values.linkToListen || "",
      promoPhotos: values.promoPhotos || "",
      promoPhotosCredit: values.promoPhotosCredit || "",
      status: "submitted",
      AppleInfo: {
        appleId: values.appleId,
        appleTrackId: values.appleTrackId,
        subgenre: Array.isArray(values.appleSubgenre) ? values.appleSubgenre.join(", ") : values.appleSubgenre,
        releaseStage: values.appleReleaseStage,
        pitchFrom: values.applePitchFrom,
        pitchTo: values.applePitchTo,
        hasTimedRelease: values.appleHasTimedRelease,
        timedReleaseDate: values.appleTimedReleaseDate,
        hasMotionArtwork: values.appleHasMotionArtwork,
        hasSpatialAudio: values.appleHasSpatialAudio,
        label: values.appleLabel,
        promotionDate: values.applePromotionDate,
        country: values.appleCountry,
      },
    });
  };

  // Create dynamic validation schema based on selected DSPs
  const validationSchema = React.useMemo(() => {
    return createPitchValidationSchema(selectedDSPs, companyTypeCategory);
  }, [selectedDSPs, companyTypeCategory]);

  if (loading) {
    /*
     * The pitch is fetched before the form can be populated, and a 128px spinner
     * on an otherwise blank screen said nothing about what was coming. This is
     * the edit form's own shell — back link, breadcrumb, then section cards of
     * fields — so the page holds its shape and the real fields replace the
     * placeholders in situ.
     */
    return (
      <div className="py-2 sm:p-6" aria-busy="true">
        <Skeleton className="w-16 h-5 mb-4" />
        <Skeleton className="h-4 mb-6 w-72" />

        {[0, 1, 2].map((section) => (
          <div
            key={section}
            className="p-4 mb-6 bg-white border rounded-lg border-neutral-200 sm:p-6"
          >
            <Skeleton className="w-48 h-6 mb-6" />
            <SkeletonFields count={6} columns={2} />
          </div>
        ))}

        <div className="flex justify-end gap-3">
          <Skeleton className="w-24 h-10 rounded-md" />
          <Skeleton className="h-10 rounded-md w-36" />
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="p-6 border border-danger-200 rounded-md bg-danger-50">
        <p className="text-danger-600">{fetchError}</p>
        <div className="flex gap-4 mt-4">
          <Button onClick={handleRetry}>
            Retry
          </Button>
          <Button variant="neutral" onClick={() => navigate("/dashboard")}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="py-2 sm:p-6">
      <Breadcrumbs items={breadcrumbItems} />

      {/* Artist Update Prompt */}
      {showArtistUpdatePrompt && artistUpdates && (
        <div className="p-4 mb-6 border border-primary-200 rounded-lg bg-primary-50">
          <div className="flex items-start space-x-3">
            <svg
              className="w-5 h-5 text-primary-400 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-primary-800">
                Artist Information Updated
              </h3>
              <p className="mt-1 text-sm text-primary-700">
                The artist "{currentPitchData?.artistName}" information has been updated in your roster.
                Would you like to update this draft with the latest artist data?
              </p>
              {artistUpdates.changes && (
                <div className="mt-2">
                  <p className="text-xs font-medium text-primary-700">Updated fields:</p>
                  <ul className="text-xs text-primary-700 list-disc list-inside">
                    {artistUpdates.changes.map((change: string, index: number) => (
                      <li key={index}>{change}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex gap-3 mt-4">
                <Button size="sm" onClick={applyArtistUpdates} loading={loading}>
                  {loading ? "Updating..." : "Update Draft"}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowArtistUpdatePrompt(false)}
                >
                  Keep Current Data
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove the Error Summary Banner section */}

      <Formik
        innerRef={formikRef}
        initialValues={initialFormValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ values, isValid, touched, setSubmitting, setFieldValue, dirty }) => {
          // The pitch can be edited in three independent places: Formik fields,
          // the platform checkboxes, and the email modal. Any of them counts as
          // a change. Drafts keep the button always enabled - "Submit Draft" is
          // a status transition, not an edit, so it must work untouched.
          const isDraftPitch =
            currentPitchData.status === 'draft' || currentPitchData.status === 'Needs Pitch';
          const hasChanges =
            isDraftPitch ||
            dirty ||
            JSON.stringify(selectedDSPs) !== baselineSelectedDSPs ||
            serializeEmailData(emailData) !== baselineEmailData;

          return (
          <Form>
            <OnInvalidSubmit
              onInvalid={(submitErrors) => {
                showErrorToast(submitErrors);
                scrollToFirstError(submitErrors);
              }}
            />
            <div className="p-5 mb-8 rounded-lg bg-neutral-50">
              <ReleaseInfo />
            </div>

            <div className="p-5 mb-8 rounded-lg bg-neutral-50">
              <PlatformSelection
                selectedDSPs={selectedDSPs}
                setSelectedDSPs={setSelectedDSPs}
                onDspDeselect={handleDspDeselect}
                onEmailSelect={handleEmailSelect}
                dspList={dspList}
              />
            </div>

            <div className="p-5 mb-8 rounded-lg bg-neutral-50">
              <GeneralReleaseInfo
                selectedDSPs={selectedDSPs}
                emailData={hasEmailData && emailData ? emailData : undefined}
              />
            </div>

            {hasDspSpecificFields(selectedDSPs) && (
              <div className="p-5 mb-8 rounded-lg bg-neutral-50">
                <DspSpecificInfo selectedDSPs={selectedDSPs} onDownloadAppleCsv={handleDownloadAppleCsv} companyType={companyType} />
              </div>
            )}

            <div className="p-5 mb-4 rounded-lg bg-neutral-50">
              <ProvisionalFieldsControl
                values={values}
                setFieldValue={setFieldValue}
                selectedDSPs={selectedDSPs}
              />
            </div>

            <div className="flex justify-end gap-4 mt-8">
              <Button
                type="button"
                variant="neutral"
                onClick={handleBack}
                disabled={isSubmitting || isDrafting}
              >
                Cancel
              </Button>

              {values.status === "draft" && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => handleSaveDraft(values)}
                  loading={isDrafting}
                  disabled={isSubmitting}
                >
                  {isDrafting ? "Saving Draft..." : "Save Changes"}
                </Button>
              )}

              {/* Edit Email - enabled for drafts and for pitches that have not sent
                  email yet; once emails exist it is disabled and points at the Email page. */}
              {selectedDSPs.email && hasEmailData && (
                values.status === "draft" || !initialFormValues.hasEmailLogs ? (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => handleEditEmail(values)}
                    disabled={isSubmitting || isDrafting}
                  >
                    <EnvelopeIcon className="w-4 h-4" />
                    Edit Email
                  </Button>
                ) : (
                <Tooltip.Provider delayDuration={150}>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      {/* wrapper span so the tooltip still fires while the button is disabled */}
                      <span className="inline-flex cursor-not-allowed">
                        {/* pointer-events-none lets the hover reach the wrapper span,
                            so the tooltip explains why the button is disabled. */}
                        <Button
                          type="button"
                          variant="secondary"
                          disabled
                          aria-disabled="true"
                          className="pointer-events-none"
                        >
                          <EnvelopeIcon className="w-4 h-4" />
                          Edit Email
                        </Button>
                      </span>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content
                        className="z-50 max-w-xs px-3 py-2 text-sm text-white break-words bg-black rounded-md shadow-lg"
                        side="top"
                        align="center"
                        sideOffset={5}
                      >
                        To send an email, go to{" "}
                        <span className="font-semibold">Email</span>{" "}
                        <span className="text-neutral-400">→</span>{" "}
                        <span className="font-semibold">Send Pitch Tab</span>{" "}
                        <span className="text-neutral-400">→</span>{" "}
                        <span className="font-semibold">Send Email</span> from there.
                        <Tooltip.Arrow className="fill-black" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
                )
              )}

              <Button
                type="submit"
                loading={isSubmitting}
                disabled={isDrafting || !hasChanges}
                title={!hasChanges ? "Make a change to enable updating" : undefined}
              >
                {isSubmitting ? "Submitting..." : isDraftPitch ? "Submit Draft" : "Update"}
              </Button>
            </div>
          </Form>
          );
        }}
      </Formik>

      {/* Provisional fields confirm modal */}
      {provisionalModalData && (
        <ProvisionalConfirmModal
          changedFields={provisionalModalData.changedFields}
          unchangedFields={provisionalModalData.unchangedFields}
          onConfirm={(stillProvisionalKeys) => {
            const payload = provisionalModalData.pendingSubmitPayload;
            const modalFieldKeys = provisionalModalData.changedFields.map(f => f.key);
            const snapshotInitial = initialFormValues;
            setProvisionalModalData(null);
            doSubmit(payload, stillProvisionalKeys, modalFieldKeys, snapshotInitial);
          }}
          onCancel={() => {
            setProvisionalModalData(null);
            setIsSubmitting(false);
          }}
        />
      )}

      {/* Email Pitch Modal - only shown when submitting a draft with email selected */}
      <EmailPitchModal
        isOpen={isEmailModalOpen}
        onClose={() => {
          setIsEmailModalOpen(false);
          setPendingFormValues(null);
          if (!hasEmailData) {
            setSelectedDSPs((prev) => ({ ...prev, email: false }));
          }
        }}
        onSend={handleEmailSend}
        pitchData={pendingFormValues}
        selectedDSPs={Object.entries(selectedDSPs)
          .filter(([_, isSelected]) => isSelected)
          .map(([platform]) => platform)}
        initialEmailData={initialEmailData}
      />

    </div>
  );
};

export default EditFormPage;